/* eslint-disable @typescript-eslint/no-explicit-any */
// ==============================
// Imports
// ==============================

// Libraries
import axios, { AxiosRequestConfig, AxiosError } from 'axios';

// Initial
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ==============================
// Token Interceptor (Request)
// ==============================
api.interceptors.request.use(
  (config) => {
    const tokens = JSON.parse(
      localStorage.getItem('aquatics_hub_2025_tokens') || 'null',
    );
    if (tokens && tokens.accessToken && config.headers) {
      config.headers['Authorization'] = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ==============================
// Refresh Token Interceptor (Response)
// ==============================
let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (err: AxiosError) => void;
}[] = [];

const processQueue = (error: AxiosError | null, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/refresh-token')
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        const tokens = JSON.parse(
          localStorage.getItem('aquatics_hub_2025_tokens') || 'null',
        );
        const refreshToken = tokens?.refreshToken;
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/auth/refresh-token`,
          { refreshToken },
        );

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          response.data.data;
        localStorage.setItem(
          'aquatics_hub_2025_tokens',
          JSON.stringify({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          }),
        );

        api.defaults.headers.common['Authorization'] =
          `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);

        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError: any) {
        processQueue(refreshError, null);

        // Clear tokens from local storage and redirect to sign-in
        localStorage.removeItem('aquatics_hub_2025_tokens');
        localStorage.removeItem('aquatics_hub_2025_user');
        window.location.href = '/signin';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// GET request
export const apiGet = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.get<T>(url, config);
  return response.data;
};

// POST request
export const apiPost = async <T = unknown, D = unknown>(
  url: string,
  data: D,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.post<T>(url, data, config);
  return response.data;
};

// PUT request
export const apiPut = async <T = unknown, D = unknown>(
  url: string,
  data: D,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.put<T>(url, data, config);
  return response.data;
};

// DELETE request
export const apiDelete = async <T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.delete<T>(url, config);
  return response.data;
};

// PATCH
export const apiPatch = async <T = unknown, D = unknown>(
  url: string,
  data: D,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await api.patch<T>(url, data, config);
  return response.data;
};

// ==============================
// Class Clone API Functions
// ==============================
import {
  Week,
  WeekDetails,
  CloneFormData,
  CloneResponse,
} from '../types/CloneClass.type';

// Get list of available weeks
export const getAvailableWeeks = async (): Promise<Week[]> => {
  const response = await apiGet<{
    code: number;
    message: string;
    data: { weeks: Week[] };
  }>('/class/weeks');
  return response.data.weeks;
};

// Get details of a specific week
export const getWeekDetails = async (
  startDate: string,
): Promise<WeekDetails> => {
  const response = await apiGet<{
    code: number;
    message: string;
    data: WeekDetails;
  }>(`/class/week/${startDate}`);
  return response.data;
};

// Clone classes from one week to another
export const cloneClasses = async (
  formData: CloneFormData,
): Promise<CloneResponse> => {
  const response = await apiPost<{
    code: number;
    message: string;
    data: CloneResponse;
  }>('/class/clone-week', formData);
  return response.data;
};
